import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { ApiError } from "@/lib/api";

import { useAddItem, usePatchCart } from "../cart/hooks";
import { QuoteEstimator, defaultEstimatorRange } from "../pricing/QuoteEstimator";
import { track } from "../lib/analytics";
import { PhotoGallery } from "./PhotoGallery";
import { PriceTierBlock } from "./PriceTierBlock";
import { SimilarEquipmentRail } from "./SimilarEquipmentRail";
import { SpecsTable } from "./SpecsTable";
import { TypeHighlights } from "./TypeHighlights";
import { usePublicType } from "./api";
import { formatDollarsRound } from "./pricing";
import type { TypeDetail } from "./types";

// FR-4 — /c/:category_slug/:type_slug. Gallery, title, pricing block,
// specs table, spec sheet PDF link, sticky-on-mobile Reserve CTA, and
// the contract §17 standing-charges disclosure. The route owns the
// type_slug; the category_slug in the URL is for SEO/breadcrumb only,
// so a mismatch with the type's actual category quietly redirects to
// the canonical URL via react-router.
export function TypeDetailPage(): JSX.Element {
  const { type_slug = "", category_slug = "" } = useParams<{
    type_slug: string;
    category_slug: string;
  }>();
  const navigate = useNavigate();
  const { data, isPending, isError, error } = usePublicType(type_slug);

  useEffect(() => {
    if (type_slug) track({ name: "type_viewed", type_slug });
  }, [type_slug]);

  // If the URL's category slug doesn't match the type's real category,
  // rewrite to the canonical path so external links don't get stuck on
  // a stale category prefix.
  useEffect(() => {
    if (data && data.category.slug && data.category.slug !== category_slug) {
      navigate(`/c/${data.category.slug}/${data.slug}`, { replace: true });
    }
  }, [data, category_slug, navigate]);

  if (isPending) return <TypeDetailSkeleton />;
  if (isError) {
    return (
      <Alert variant="destructive">
        We couldn&rsquo;t load this equipment.
        {error instanceof ApiError ? ` (HTTP ${error.status})` : null}
      </Alert>
    );
  }
  if (!data) return <TypeNotFound />;

  return <TypeDetailBody type={data} />;
}

function TypeDetailBody({ type }: { type: TypeDetail }): JSX.Element {
  // Lift the Quick estimate's date range so the Reserve button can
  // carry it into the cart — otherwise the customer picks dates,
  // sees an estimate, clicks Reserve, and lands on /cart with empty
  // date fields.
  const defaults = useMemo(
    () => defaultEstimatorRange(type.notice_required_hours),
    [type.notice_required_hours]
  );
  const [startDate, setStartDate] = useState(defaults.start);
  const [endDate, setEndDate] = useState(defaults.end);

  return (
    <div className="pb-24 md:pb-0">
      <nav aria-label="Breadcrumb" className="mb-6 text-sm text-zinc-600">
        <Link to="/" className="hover:text-brand-red hover:underline">Home</Link>
        <span className="mx-1 text-zinc-400" aria-hidden="true">/</span>
        <Link to={`/c/${type.category.slug}`} className="hover:text-brand-red hover:underline">
          {type.category.name}
        </Link>
        <span className="mx-1 text-zinc-400" aria-hidden="true">/</span>
        <span className="text-brand-black">{type.name}</span>
      </nav>

      <div className="grid gap-8 md:grid-cols-2 md:gap-10">
        <PhotoGallery photos={type.photos} typeName={type.name} />

        <div className="space-y-6">
          <header>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-red">
              {type.category.name}
            </p>
            <h1 className="mt-2 font-serif text-3xl font-bold italic tracking-tight text-brand-black md:text-4xl">
              {type.name}
            </h1>
            <span aria-hidden="true" className="mt-3 inline-block h-0.5 w-12 bg-brand-red" />
            {type.summary ? (
              <p className="mt-3 text-sm text-zinc-700 sm:text-base">{type.summary}</p>
            ) : null}
          </header>

          <PriceTierBlock type={type} />

          <QuoteEstimator
            type={type}
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />

          {type.spec_sheet_url ? (
            <p>
              <a
                href={type.spec_sheet_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-red transition hover:text-brand-red-dark hover:underline"
              >
                Download spec sheet (PDF)
                <span aria-hidden="true">↗</span>
              </a>
            </p>
          ) : null}

          <div className="hidden md:block">
            <ReserveButton type={type} startDate={startDate} endDate={endDate} />
          </div>
        </div>
      </div>

      <TypeHighlights type={type} />

      {type.specs && Object.keys(type.specs).length > 0 ? (
        <section className="mt-12" aria-labelledby="specs-heading">
          <h2
            id="specs-heading"
            className="font-serif text-xl font-bold italic tracking-tight text-brand-black md:text-2xl"
          >
            Specs
          </h2>
          <span aria-hidden="true" className="mt-2 mb-4 inline-block h-0.5 w-10 bg-brand-red" />
          <SpecsTable specs={type.specs} />
        </section>
      ) : null}

      {type.description ? (
        <section className="mt-12" aria-labelledby="overview-heading">
          <h2
            id="overview-heading"
            className="font-serif text-xl font-bold italic tracking-tight text-brand-black md:text-2xl"
          >
            More about this {type.category.name.toLowerCase().replace(/s$/, "")}
          </h2>
          <span aria-hidden="true" className="mt-2 inline-block h-0.5 w-10 bg-brand-red" />
          <p className="mt-3 max-w-3xl whitespace-pre-line text-sm text-zinc-700 sm:text-base">
            {type.description}
          </p>
        </section>
      ) : null}

      <SimilarEquipmentRail type={type} />

      <FeeDisclosure type={type} />

      {/* Sticky mobile-only Reserve bar. iOS keyboard avoidance is left
          to the page chrome — the input fields here are server-side
          read-only so the bar doesn't intercept input focus. */}
      <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-zinc-200 bg-brand-white p-3 shadow-[0_-2px_8px_rgba(0,0,0,0.05)] md:hidden">
        <ReserveButton type={type} startDate={startDate} endDate={endDate} fullWidth />
      </div>
    </div>
  );
}

// "Reserve this equipment" pushes the type into the customer's cart
// and lands them on /cart. The cart is server-side (anonymous via
// signed cookie until inline signup) — see app/models/cart.rb. We
// add a single line per click; quantity > 1 is achieved by re-adding
// since the rental engine binds each line to its own unit at finalize.
function ReserveButton({
  type,
  startDate,
  endDate,
  fullWidth = false
}: {
  type: TypeDetail;
  startDate: string;
  endDate: string;
  fullWidth?: boolean;
}): JSX.Element {
  const navigate = useNavigate();
  const addItem = useAddItem();
  const patchCart = usePatchCart();
  const pending = addItem.isPending || patchCart.isPending;
  return (
    <Button
      type="button"
      size="lg"
      className={fullWidth ? "w-full" : ""}
      disabled={pending}
      onClick={() => {
        track({ name: "reserve_clicked", type_slug: type.slug });
        addItem.mutate(
          {
            type_id: type.id,
            damage_waiver: false,
            attachment_type_ids: []
          },
          {
            onSuccess: () => {
              // Carry the Quick-estimate date range into the cart so
              // the customer doesn't have to re-pick dates and the
              // cart's price estimate matches the quote they were
              // just looking at. Cart's own DatesSection still owns
              // subsequent edits. onSettled (not onSuccess) so a
              // PATCH failure still lands the user on /cart — the
              // item is in the cart either way and the cart's date
              // picker is the fallback.
              patchCart.mutate(
                { delivery_date_start: startDate, delivery_date_end: endDate },
                { onSettled: () => navigate("/cart") }
              );
            }
          }
        );
      }}
    >
      {pending ? "Adding…" : "Reserve this equipment"}
    </Button>
  );
}

// Replaces the prior vague "tax/surcharge computed at checkout" line
// with concrete numbers. Sales tax + environmental services charge are
// per-jurisdiction so we keep "applicable" wording on those, but we
// quote the type's actual delivery fee and the standing card surcharge
// rate so first-time renters don't read the page as bait-and-switch.
function FeeDisclosure({ type }: { type: TypeDetail }): JSX.Element {
  const deliveryFee = type.delivery_fee_cents > 0 ? formatDollarsRound(type.delivery_fee_cents) : null;
  return (
    <p className="mt-10 max-w-3xl text-xs text-zinc-500">
      Final total at checkout adds applicable Colorado sales tax,
      {deliveryFee ? <> a delivery fee from {deliveryFee} (within the standard service area),</> : null}
      {" "}an environmental services charge, and a 3% surcharge if you pay by card. ACH and Check skip the surcharge.
    </p>
  );
}

function TypeDetailSkeleton(): JSX.Element {
  return (
    <div className="space-y-4" aria-busy="true">
      <Skeleton className="h-4 w-1/2" />
      <div className="grid gap-6 md:grid-cols-2">
        <Skeleton className="aspect-[4/3] w-full" />
        <div className="space-y-3">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    </div>
  );
}

function TypeNotFound(): JSX.Element {
  return (
    <div className="rounded-md border border-zinc-200 bg-brand-white p-8 text-center">
      <h1 className="text-xl font-semibold text-brand-black">Equipment not found</h1>
      <p className="mt-2 text-sm text-zinc-600">
        The URL may be stale or this equipment was archived.
      </p>
      <p className="mt-4">
        <Link to="/" className="text-brand-red hover:underline">Browse all categories</Link>
      </p>
    </div>
  );
}
